import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";
import { useMemo } from "react";
import { Currency, ExpenseType } from "@/gql/graphql";
import dayjs from "dayjs";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { reportWorkOrderDetailQuery } from "./GraphQL/reportWorkOrdersQuery";
import { workOrderMessages } from "@/app/ProDrivers/WorkOrders/messages/workOrderMessages";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";

const expenseTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 11,
  headerFontSize: 12,
  spacing: 3,
});

interface WorkOrderExpenseDetailsProps {
  workOrderId: string;
  workOrderCurrency: Currency;
}

interface FlattenedExpense {
  id: string;
  driver_name: string;
  type: ExpenseType;
  description: string | null | undefined;
  currency: Currency;
  amount: number;
  payout_amount: number;
}

interface FlattenedPayout {
  id: string;
  driver_name: string;
  currency: Currency;
  amount: number;
  amount_tax: number;
  converted_amount: number | null | undefined;
  converted_currency: Currency | null | undefined;
  transaction_reference: string | null | undefined;
  paid_at: string | null | undefined;
}

export function WorkOrderExpenseDetails({
  workOrderId,
  workOrderCurrency,
}: WorkOrderExpenseDetailsProps) {
  const { data, isLoading } = useGqlQuery(reportWorkOrderDetailQuery, {
    id: workOrderId,
  });

  const { expenses, payouts } = useMemo(() => {
    if (!data?.workOrder) return { expenses: [], payouts: [] };

    const flatExpenses: FlattenedExpense[] = [];
    const flatPayouts: FlattenedPayout[] = [];

    for (const assignment of data.workOrder.assignments) {
      const driverName = assignment.proDriver.name ?? "—";

      for (const expense of assignment.expenses) {
        flatExpenses.push({
          id: expense.id,
          driver_name: driverName,
          type: expense.type,
          description: expense.description,
          currency: expense.currency,
          amount: expense.amount,
          payout_amount: expense.payout_amount,
        });
      }

      for (const payout of assignment.payouts) {
        flatPayouts.push({
          id: payout.id,
          driver_name: driverName,
          currency: payout.currency,
          amount: payout.amount,
          amount_tax: payout.amount_tax,
          converted_amount: payout.converted_amount,
          converted_currency: payout.converted_currency,
          transaction_reference: payout.transaction_reference,
          paid_at: payout.paid_at,
        });
      }
    }

    return { expenses: flatExpenses, payouts: flatPayouts };
  }, [data]);

  const expenseColumns = useMemo(() => {
    return [
      {
        field: "driver_name",
        headerName: "Driver",
        flex: 1,
      },
      {
        field: "type",
        headerName: "Type",
        flex: 1,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const type = params.value as ExpenseType;
          return (
            workOrderMessages.expenseType[type] ??
            params.value.replace(/_/g, " ")
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 100,
      },
      {
        field: "amount",
        headerName: "Amount (Original)",
        flex: 1,
        valueFormatter: (params) => {
          const expense = params.data;
          return formatCurrency(params.value, expense?.currency as Currency, {
            emptyValue: "",
          });
        },
      },
      {
        field: "payout_amount",
        headerName: "Claimable",
        flex: 1,
        valueFormatter: (params) => {
          const expense = params.data;
          return formatCurrency(params.value, expense?.currency as Currency, {
            emptyValue: "",
          });
        },
      },
    ] as ColDef<FlattenedExpense>[];
  }, []);

  const payoutColumns = useMemo(() => {
    return [
      {
        field: "driver_name",
        headerName: "Driver",
        flex: 1,
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 100,
      },
      {
        field: "amount",
        headerName: "Amount (Original)",
        flex: 1,
        valueFormatter: (params) => {
          const payout = params.data;
          return formatCurrency(params.value, payout?.currency as Currency, {
            emptyValue: "",
          });
        },
      },
      {
        field: "converted_amount",
        headerName: `Amount (${workOrderCurrency})`,
        flex: 1,
        valueFormatter: (params) => {
          const payout = params.data;
          const currency = (payout?.converted_currency ??
            workOrderCurrency) as Currency;
          return formatCurrency(params.value, currency, {
            emptyValue: "",
          });
        },
      },
      {
        field: "transaction_reference",
        headerName: "Transaction Ref",
        flex: 1,
      },
      {
        field: "paid_at",
        headerName: "Paid At",
        flex: 1,
        valueFormatter: (params) => {
          if (!params.value) return "";
          return dayjs(params.value).format("DD/MM/YYYY");
        },
      },
    ] as ColDef<FlattenedPayout>[];
  }, [workOrderCurrency]);

  if (isLoading) {
    return (
      <div className="bg-gray-50 p-4 text-center text-sm text-gray-500">
        Loading expenses and payouts...
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4">
      {expenses.length === 0 && payouts.length === 0 ? (
        <div className="text-center text-gray-500">
          No expenses or payouts recorded for this work order
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {expenses.length > 0 ? (
            <div>
              <h4 className="mb-3 text-sm font-medium text-gray-900">
                Expenses ({expenses.length} expense
                {expenses.length !== 1 ? "s" : ""})
              </h4>
              <div className="h-48">
                <AgGridReact<FlattenedExpense>
                  rowData={expenses}
                  columnDefs={expenseColumns}
                  theme={expenseTheme}
                  suppressMovableColumns={true}
                  suppressColumnVirtualisation={true}
                  headerHeight={32}
                  rowHeight={28}
                />
              </div>
            </div>
          ) : null}
          {payouts.length > 0 ? (
            <div>
              <h4 className="mb-3 text-sm font-medium text-gray-900">
                Payouts ({payouts.length} payout
                {payouts.length !== 1 ? "s" : ""})
              </h4>
              <div className="h-48">
                <AgGridReact<FlattenedPayout>
                  rowData={payouts}
                  columnDefs={payoutColumns}
                  theme={expenseTheme}
                  suppressMovableColumns={true}
                  suppressColumnVirtualisation={true}
                  headerHeight={32}
                  rowHeight={28}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
